import * as React from 'react';
import * as moment from 'moment';
import * as shortid from 'shortid';
import * as ellipsize from 'ellipsize';
import { Icon, Tag, Tooltip } from '../';

interface Props {
  year: number;
  month: number;
  bookings?: Array<{ start: any; end: any; status: string }>;
  holidays?: Array<{ start: any; end: any; name: string }>;
  reservations?: Array<{ start: any; end: any }>;
  handleClick: any;
  ready: boolean;
}

interface State {
  weeks: number;
  start: any;
}

class Calendar extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      weeks: 0,
      start: false,
    };
  }

  componentDidMount() {
    this.calculate();
  }

  componentWillReceiveProps() {
    this.calculate();
  }

  /**
   * Calculate calendar dates
   */
  calculate() {
    const start = moment([this.props.year, this.props.month]);
    const end = moment(start).endOf('month');

    // Get week-rounded start date of month
    if (start.isoWeekday() - 1 > 0) {
      start.subtract(start.isoWeekday() - 1, 'd');
    }

    // Get week-rounded end date of month
    if (7 - (end.isoWeekday() - 1) > 0) {
      end.add(7 - (end.isoWeekday() - 1), 'd');
    }

    // Get number of weeks in the month
    const weeks = end.diff(start, 'week');

    this.setState({ weeks, start });
  }

  /**
   * Render the key of the calendar at the top
   */
  renderKey() {
    const row = [];
    const days = [
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
      'Sunday',
    ];
    for (let i = 0; i < 7; i++) {
      row.push(
        <div key={i} className="column has-text-centered">
          <small>
            <strong>{days[i]}</strong>
          </small>
        </div>,
      );
    }
    return row;
  }

  /**
   * Render a single day
   */
  renderDay(date, animationDelay = 0) {
    const currentDate = date.clone();

    // Determine if there are any hires or cancellations
    let hires = 0;
    let cancellations = 0;

    if (this.props.bookings !== undefined) {
      const endDate = date.clone().endOf('day');
      const monthBookings = this.props.bookings.filter((booking) => {
        const start = moment(booking.start);
        const end = moment(booking.end);
        return endDate.diff(start) >= 0 && date.diff(end) <= 0;
      });

      hires = monthBookings.filter((booking) => {
        return booking.status !== 'CANCELLED';
      }).length;
      cancellations = monthBookings.filter((booking) => {
        return booking.status === 'CANCELLED';
      }).length;
    }

    // Determine if there are any reservations
    let reservations = [];
    if (this.props.reservations !== undefined) {
      const endDate = date.clone().endOf('day');
      reservations = this.props.reservations.filter((reservation) => {
        const start = moment(reservation.start);
        const end = moment(reservation.end);
        return endDate.diff(start) >= 0 && date.diff(end) <= 0;
      });
    }

    // Determine if there are any holidays
    const holidays = [];
    if (this.props.holidays !== undefined) {
      this.props.holidays.forEach((holiday) => {
        const start = moment(holiday.start);
        const end = moment(holiday.end);
        const endDate = date.clone().set({
          hour: 23,
          minute: 59,
          second: 59,
          millisecond: 999,
        });
        if (endDate.diff(start) >= 0 && date.diff(end) <= 0) {
          holidays.push(holiday.name);
        }
      });
    }

    // Determine what classes to assign
    const classes = ['calendar--day'];
    const isToday = date.isSame(new Date(), 'd');
    if (isToday) {
      classes.push('is-today');
    }
    const isCurrentMonth = date.month() === this.props.month;
    if (!isCurrentMonth) {
      classes.push('is-disabled is-hidden-mobile');
    } else if (holidays.length > 0) {
      classes.push('has-holiday');
    }

    const day = (
      <div
        key={shortid.generate()}
        className={classes.join(' ')}
        style={{ animationDelay: `${animationDelay}s` }}
        onClick={() => {
          if (isCurrentMonth) {
            this.props.handleClick(currentDate);
          }
        }}
      >
        <div className="calendar--meta is-hidden-mobile">
          <small className="calendar--meta__day">
            {moment(date).format('D')}
          </small>
          {isCurrentMonth && (
            <div className="tags has-addons has-text-centered">
              {hires > 0 && (
                <Tag>
                  <Icon size="small" icon="truck" />
                  <span>{hires}</span>
                </Tag>
              )}
              {cancellations > 0 && (
                <Tag type="danger">
                  <Icon size="small" icon="times" />
                  <span>{cancellations}</span>
                </Tag>
              )}
              {reservations.length > 0 && (
                <Tag type="dark">
                  <Icon size="small" icon="lock" />
                  <span>{reservations.length}</span>
                </Tag>
              )}
            </div>
          )}
        </div>
        {isCurrentMonth && (
          <div className="is-hidden-tablet">
            <div className="field">
              {holidays.length > 0 &&
                holidays.map((holiday, index) => (
                  <Tag key={index} type="dark" className="is-pulled-right">
                    <Icon icon="calendar" />
                    <span>{holiday}</span>
                  </Tag>
                ))}
              <small>{moment(date).format('dddd Do MMMM')}</small>
            </div>
            <div className="tags">
              {hires > 0 && (
                <Tag className="is-medium">
                  <Icon icon="truck" />
                  <span>{hires} on hire</span>
                </Tag>
              )}
              {cancellations > 0 && (
                <Tag type="danger" className="is-medium">
                  <Icon icon="times" />
                  <span>{cancellations} cancelled</span>
                </Tag>
              )}
              {reservations.length > 0 && (
                <Tag type="dark" className="is-medium">
                  <Icon icon="lock" />
                  <span>{reservations.length} reserved</span>
                </Tag>
              )}
            </div>
          </div>
        )}
      </div>
    );

    // Show holiday tooltip if has any
    if (isCurrentMonth && holidays.length > 0) {
      return (
        <Tooltip
          key={shortid.generate()}
          position="top column"
          text={ellipsize(holidays.join(', '), 50)}
        >
          {day}
        </Tooltip>
      );
    }

    return (
      <div key={shortid.generate()} className="column">
        {day}
      </div>
    );
  }

  /**
   * Render a row of days
   */
  renderWeek(startDate, weekIndex) {
    const week = [];
    let date = startDate;
    for (let i = 0; i < 7; i++) {
      week.push(this.renderDay(date, weekIndex * (3 / 32) + i / 32));
      date = date.add(1, 'd');
    }
    return week;
  }

  /**
   * Render the final calendar
   */
  render() {
    const calendar = [];
    for (let i = 0; i < this.state.weeks; i++) {
      calendar.push(
        <div key={i} className="calendar--week columns is-marginless">
          {this.renderWeek(this.state.start.clone().add(i, 'w'), i)}
        </div>,
      );
    }
    return (
      <div className="calendar">
        <div className="calendar--key columns is-gapless is-marginless is-hidden-mobile">
          {this.renderKey()}
        </div>
        <div className="calendar--content">{this.props.ready && calendar}</div>
      </div>
    );
  }
}

export default Calendar;
